'use client';

import { useEffect, useState } from 'react';
import { ArrowRight } from 'lucide-react';
import { cn } from '@/components/ui/utils';
import type { BlogSection } from '../data/blogs';
import BlogShareLinks from './BlogShareLinks';

interface BlogTableOfContentsProps {
  sections: BlogSection[];
  shareUrl?: string;
  shareTitle?: string;
}

export default function BlogTableOfContents({
  sections,
  shareUrl,
  shareTitle,
}: BlogTableOfContentsProps) {
  const [activeId, setActiveId] = useState(sections[0]?.id ?? '');

  useEffect(() => {
    const elements = sections
      .map((section) => document.getElementById(section.id))
      .filter((element): element is HTMLElement => Boolean(element));

    if (elements.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visibleEntries = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);

        if (visibleEntries[0]?.target.id) {
          setActiveId(visibleEntries[0].target.id);
        }
      },
      {
        rootMargin: '-18% 0px -68% 0px',
        threshold: [0, 1],
      },
    );

    elements.forEach((element) => observer.observe(element));
    return () => observer.disconnect();
  }, [sections]);

  return (
    <aside className="rounded-2xl bg-white p-4 shadow-[0_18px_45px_-32px_rgba(0,0,0,0.45)] sm:p-5 lg:max-h-[calc(100vh-var(--site-header-height)-3rem)] lg:overflow-auto lg:p-6">
      <h2 className="text-xl font-bold leading-tight text-black sm:text-2xl">Table of Content</h2>
      <div className="mt-4 h-px w-full bg-black/25 sm:mt-5" />

      <nav className="mt-4 max-h-64 space-y-4 overflow-auto pr-1 sm:mt-5 sm:space-y-5 lg:max-h-none lg:overflow-visible lg:pr-0">
        {sections.map((section) => {
          const isActive = activeId === section.id;

          return (
            <a
              key={section.id}
              href={`#${section.id}`}
              className={cn(
                'group flex items-start gap-3 text-sm leading-relaxed text-[#777777] transition hover:text-black sm:text-base',
                isActive && 'font-medium text-black',
              )}
            >
              <span
                className={cn(
                  'mt-0.5 hidden h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#ed1c22] text-white sm:mt-1',
                  isActive && 'flex',
                )}
                aria-hidden
              >
                <ArrowRight className="h-4 w-4" strokeWidth={2.4} />
              </span>
              <span>{section.title}</span>
            </a>
          );
        })}
      </nav>

      {shareUrl && shareTitle ? (
        <BlogShareLinks url={shareUrl} title={shareTitle} className="sm:mt-6" />
      ) : null}
    </aside>
  );
}
